// @ts-check
/** @typedef {import('../types/api').Vendor} Vendor */

import { Copy, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import vendorService from '../services/vendorService';

const emptyOrderItem = () => ({
  description: '',
  quantity: 1,
  unit: 'pieces',
  unit_price: 0,
  custom_fields: {},
});

const CreateVendorOrderPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const duplicateOrderId = searchParams.get('duplicate');
  const { showToast } = useToast();
  /** @type {[Vendor[], Function]} */
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [duplicatedFrom, setDuplicatedFrom] = useState(null);
  const [lastVendorOrder, setLastVendorOrder] = useState(null);
  const taxManuallyEdited = useRef(false);
  const [formData, setFormData] = useState({
    vendor_id: '',
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    priority: 'medium',
    notes: '',
    tax_percentage: 18,
  });
  const [orderItems, setOrderItems] = useState([emptyOrderItem()]);

  // Pre-fill the whole form from an existing order (via "Duplicate Order" on the detail page).
  const applyOrderToForm = (order) => {
    setFormData((prev) => ({
      ...prev,
      vendor_id: order.vendor_id,
      priority: order.priority || 'medium',
      notes: order.notes || '',
      tax_percentage: order.tax_percentage ?? 18,
    }));
    taxManuallyEdited.current = true;
    setOrderItems(
      (order.order_items || []).length > 0
        ? order.order_items.map((item) => ({
            description: item.description || '',
            quantity: item.quantity || 1,
            unit: item.unit || 'pieces',
            unit_price: item.unit_price || 0,
            custom_fields: item.custom_fields || {},
          }))
        : [emptyOrderItem()],
    );
  };

  useEffect(() => {
    if (!duplicateOrderId) return;
    vendorService
      .getVendorOrder(duplicateOrderId)
      .then((order) => {
        applyOrderToForm(order);
        setDuplicatedFrom(order.order_number);
      })
      .catch((err) => showToast(err.message, 'error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicateOrderId]);

  // Offer to copy items from this vendor's most recent order — most vendor POs are routine reorders.
  useEffect(() => {
    if (!formData.vendor_id) {
      setLastVendorOrder(null);
      return undefined;
    }
    let cancelled = false;
    vendorService
      .getVendorOrders({ vendor_id: formData.vendor_id })
      .then((orders) => {
        if (cancelled) return;
        const mostRecent = orders.find((o) => o.id !== duplicateOrderId) || null;
        setLastVendorOrder(mostRecent);
        // Tax rate is usually stable per vendor — default to what was last used, unless the admin already typed one.
        if (mostRecent?.tax_percentage != null && !taxManuallyEdited.current) {
          setFormData((prev) => ({ ...prev, tax_percentage: mostRecent.tax_percentage }));
        }
      })
      .catch(() => {
        if (!cancelled) setLastVendorOrder(null);
      });
    return () => {
      cancelled = true;
    };
  }, [formData.vendor_id, duplicateOrderId]);

  const vendorTypes = {
    fabric_supplier: {
      label: 'Fabric Supplier',
      fields: [
        { key: 'material_type', label: 'Material Type', placeholder: 'e.g., Silk, Cotton, Linen' },
        { key: 'color', label: 'Color', placeholder: 'e.g., Red, Blue' },
        { key: 'quality_grade', label: 'Quality Grade', placeholder: 'e.g., Grade A' },
        { key: 'width', label: 'Width (inches)', placeholder: 'e.g., 60' },
      ],
      defaultUnit: 'meters',
    },
    manufacturer: {
      label: 'Manufacturer',
      fields: [
        { key: 'product_name', label: 'Product Name', placeholder: 'e.g., Silk Dress' },
        { key: 'sku', label: 'SKU/Style Number', placeholder: 'e.g., KY-001' },
        { key: 'specifications', label: 'Specifications', placeholder: 'Size, color, etc.' },
        { key: 'sample_reference', label: 'Sample Reference', placeholder: 'Reference code' },
      ],
      defaultUnit: 'pieces',
    },
    printing_dyeing: {
      label: 'Printing & Dyeing',
      fields: [
        { key: 'fabric_type', label: 'Fabric Type', placeholder: 'e.g., Cotton' },
        { key: 'color_code', label: 'Color Code/Pantone', placeholder: 'e.g., Pantone 19-4052' },
        { key: 'process_type', label: 'Process Type', placeholder: 'Digital/Screen/Reactive' },
      ],
      defaultUnit: 'meters',
    },
    packaging: {
      label: 'Packaging',
      fields: [
        { key: 'box_type', label: 'Box/Bag Type', placeholder: 'e.g., Corrugated Box' },
        { key: 'dimensions', label: 'Dimensions (L×W×H)', placeholder: 'e.g., 30×20×10 cm' },
        { key: 'material', label: 'Material', placeholder: 'e.g., Cardboard' },
        { key: 'print_spec', label: 'Print Specification', placeholder: 'Logo, design details' },
      ],
      defaultUnit: 'pieces',
    },
    logistics: {
      label: 'Logistics',
      fields: [
        { key: 'pickup_location', label: 'Pickup Location', placeholder: 'Full address' },
        { key: 'delivery_location', label: 'Delivery Location', placeholder: 'Full address' },
        { key: 'weight', label: 'Weight (kg)', placeholder: 'Total weight' },
        { key: 'delivery_type', label: 'Delivery Type', placeholder: 'Express/Standard' },
      ],
      defaultUnit: 'trips',
    },
    marketing: {
      label: 'Marketing',
      fields: [
        { key: 'campaign_type', label: 'Campaign Type', placeholder: 'Social Media/Print/Digital' },
        { key: 'duration', label: 'Duration/Timeline', placeholder: 'e.g., 30 days' },
        { key: 'deliverables', label: 'Deliverables', placeholder: 'List of deliverables' },
        { key: 'target_audience', label: 'Target Audience', placeholder: 'Demographics' },
      ],
      defaultUnit: 'campaigns',
    },
  };

  const fetchVendors = useCallback(async () => {
    try {
      const data = await vendorService.getVendors({ status: 'active' });
      setVendors(data);
    } catch (error) {
      showToast(error.message, 'error');
    }
  }, [showToast]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const selectedVendor = vendors.find((v) => v.id === formData.vendor_id);
  const vendorTypeConfig = selectedVendor ? vendorTypes[selectedVendor.vendor_type] : null;

  const addOrderItem = () => {
    setOrderItems([
      ...orderItems,
      {
        description: '',
        quantity: 1,
        unit: vendorTypeConfig?.defaultUnit || 'pieces',
        unit_price: 0,
        custom_fields: {},
      },
    ]);
  };

  const removeOrderItem = (index) => {
    if (orderItems.length > 1) {
      setOrderItems(orderItems.filter((_, i) => i !== index));
    }
  };

  const updateOrderItem = (index, field, value) => {
    const updated = [...orderItems];
    if (field.startsWith('custom_')) {
      const customKey = field.replace('custom_', '');
      updated[index].custom_fields[customKey] = value;
    } else {
      updated[index][field] = value;
    }
    setOrderItems(updated);
  };

  const calculateItemTotal = (item) => {
    return item.quantity * item.unit_price;
  };

  const calculateSubtotal = () => {
    return orderItems.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  };

  const calculateTax = () => {
    const subtotal = calculateSubtotal();
    return (subtotal * formData.tax_percentage) / 100;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.vendor_id) {
      showToast('Please select a vendor', 'error');
      return;
    }

    if (orderItems.some((item) => !item.description || item.quantity <= 0 || item.unit_price <= 0)) {
      showToast('Please fill all item details with valid values', 'error');
      return;
    }

    setLoading(true);

    try {
      const orderData = {
        vendor_id: formData.vendor_id,
        order_date: formData.order_date,
        expected_delivery_date: formData.expected_delivery_date || null,
        priority: formData.priority,
        notes: formData.notes,
        order_items: orderItems,
        subtotal: calculateSubtotal(),
        tax_percentage: formData.tax_percentage,
        tax_amount: calculateTax(),
        total_amount: calculateTotal(),
      };

      await vendorService.createVendorOrder(orderData);
      showToast('Vendor order created successfully!', 'success');
      navigate('/vendors/orders');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground">Create Vendor Order</h1>
        <p className="text-muted-foreground mt-1">Create a new purchase order for a vendor</p>
        {duplicatedFrom && (
          <p className="mt-2 text-sm text-blue-800 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-900 rounded-lg px-4 py-2 inline-block">
            Pre-filled from order <strong>{duplicatedFrom}</strong> — review and adjust before creating.
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Order Details Card */}
        <div className="bg-card rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Order Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1">Vendor *</label>
              <select
                required
                value={formData.vendor_id}
                onChange={(e) => setFormData({ ...formData, vendor_id: e.target.value })}
                className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
              >
                <option value="">Select Vendor</option>
                {vendors.map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.vendor_name} ({vendor.vendor_initials})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1">Order Date *</label>
              <input
                type="date"
                required
                value={formData.order_date}
                onChange={(e) => setFormData({ ...formData, order_date: e.target.value })}
                className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1">Expected Delivery Date</label>
              <input
                type="date"
                value={formData.expected_delivery_date}
                onChange={(e) => setFormData({ ...formData, expected_delivery_date: e.target.value })}
                className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1">Priority *</label>
              <select
                required
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground/80 mb-1">Order Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
                placeholder="Add any special instructions or notes..."
              />
            </div>
          </div>
        </div>

        {/* Order Items Card */}
        <div className="bg-card rounded-lg shadow p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-xl font-semibold text-foreground">Order Items</h2>
            <button
              type="button"
              onClick={addOrderItem}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition text-sm"
            >
              <Plus size={16} />
              Add Item
            </button>
          </div>

          {!selectedVendor && (
            <div className="bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-900 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                Please select a vendor first to see vendor-specific fields for order items.
              </p>
            </div>
          )}

          {selectedVendor && lastVendorOrder && (
            <div className="bg-muted/50 border border-border rounded-lg p-4 mb-4 flex items-center justify-between gap-4">
              <p className="text-sm text-foreground/80">
                {selectedVendor.vendor_name}'s last order was <strong>{lastVendorOrder.order_number}</strong> on{' '}
                {new Date(lastVendorOrder.order_date).toLocaleDateString()} — most reorders are routine repeats.
              </p>
              <button
                type="button"
                onClick={() => {
                  applyOrderToForm(lastVendorOrder);
                  setLastVendorOrder(null);
                }}
                className="flex items-center gap-2 px-3 py-1.5 border border-input rounded-lg hover:bg-card transition text-sm shrink-0"
              >
                <Copy size={14} />
                Copy Items From It
              </button>
            </div>
          )}

          <div className="space-y-4">
            {orderItems.map((item, index) => (
              <div key={index} className="border border-border rounded-lg p-4 relative">
                {orderItems.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeOrderItem(index)}
                    className="absolute top-2 right-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                  >
                    <Trash2 size={18} />
                  </button>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-foreground/80 mb-1">Description *</label>
                    <input
                      type="text"
                      required
                      value={item.description}
                      onChange={(e) => updateOrderItem(index, 'description', e.target.value)}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
                      placeholder="Item description"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-1">Quantity *</label>
                    <input
                      type="number"
                      required
                      min="0.01"
                      step="0.01"
                      value={item.quantity}
                      onChange={(e) => updateOrderItem(index, 'quantity', parseFloat(e.target.value))}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-1">Unit *</label>
                    <input
                      type="text"
                      required
                      value={item.unit}
                      onChange={(e) => updateOrderItem(index, 'unit', e.target.value)}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
                      placeholder="e.g., meters, pieces, kg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-1">Unit Price (₹) *</label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      value={item.unit_price}
                      onChange={(e) => updateOrderItem(index, 'unit_price', parseFloat(e.target.value))}
                      className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Vendor-Specific Fields */}
                {vendorTypeConfig && (
                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm font-medium text-foreground/80 mb-3">
                      {vendorTypeConfig.label} Specific Fields:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {vendorTypeConfig.fields.map((field) => (
                        <div key={field.key}>
                          <label className="block text-sm font-medium text-muted-foreground mb-1">{field.label}</label>
                          <input
                            type="text"
                            value={item.custom_fields[field.key] || ''}
                            onChange={(e) => updateOrderItem(index, `custom_${field.key}`, e.target.value)}
                            placeholder={field.placeholder}
                            className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent text-sm"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Item Total */}
                <div className="mt-4 pt-4 border-t flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Item Total:</span>
                  <span className="text-lg font-semibold text-foreground">
                    ₹{calculateItemTotal(item).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Order Summary Card */}
        <div className="bg-card rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Order Summary</h2>

          <div className="space-y-3">
            <div className="flex justify-between text-foreground/80">
              <span>Subtotal:</span>
              <span className="font-medium">
                ₹{calculateSubtotal().toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            </div>

            <div className="flex justify-between items-center text-foreground/80">
              <div className="flex items-center gap-2">
                <span>Tax:</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.tax_percentage}
                  onChange={(e) => {
                    taxManuallyEdited.current = true;
                    setFormData({ ...formData, tax_percentage: parseFloat(e.target.value) });
                  }}
                  className="w-20 px-2 py-1 border border-input rounded-lg text-sm"
                />
                <span>%</span>
              </div>
              <span className="font-medium">
                ₹{calculateTax().toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            </div>

            <div className="pt-3 border-t flex justify-between text-lg font-bold text-foreground">
              <span>Total Amount:</span>
              <span>₹{calculateTotal().toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/vendors/orders')}
            className="px-6 py-3 border border-input rounded-lg hover:bg-muted/50 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Order'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateVendorOrderPage;
